import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import Button from "../../components/ui/button/Button";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Form from "../../components/form/Form";
import Select from "../../components/form/Select";
import MultiSelect from "../../components/form/MultiSelect";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import { CalenderIcon, PlusIcon, TrashBinIcon } from "../../icons";

const apiUrl = process.env.REACT_APP_API_URL;

type MultiSelectOption = {
  value: string;
  text: string;
  selected: boolean;
};

type MenuItemPhoto = {
  previewUrl: string;
  file: File | null;
};

type SpecialOccasionMenu = {
  id: number;
  eventName: string;
  startDate: string;
  endDate: string;
  venue: string[];
  venueOutlet: string[];
  mealType: string;
  items: {
    name: string;
    photos: MenuItemPhoto[];
    dietaries: string[];
    membersPrice: string;
    nonMembersPrice: string;
  }[];
};

const MAX_PHOTO_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

const storageKey = "specialOccasionMenus";

const loadMenus = (): SpecialOccasionMenu[] => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((menu) => {
      if (!menu || typeof menu !== "object") return menu;
      if (Array.isArray(menu.items)) {
        const normalizedItems = menu.items.map((item: any) => ({
          name: typeof item?.name === "string" ? item.name : "",
          photos: Array.isArray(item?.photos)
            ? item.photos
                .map((photo: any) => {
                  if (typeof photo === "string") return { previewUrl: photo, file: null };
                  if (photo && typeof photo === "object" && typeof photo.previewUrl === "string") {
                    return { previewUrl: photo.previewUrl, file: null };
                  }
                  return null;
                })
                .filter(Boolean)
            : Array.isArray(item?.photoDataUrls)
              ? item.photoDataUrls
                  .map((photo: any) =>
                    typeof photo === "string" ? { previewUrl: photo, file: null } : null
                  )
                  .filter(Boolean)
              : typeof item?.photoDataUrl === "string" && item.photoDataUrl
                ? [{ previewUrl: item.photoDataUrl, file: null }]
                : [],
          dietaries: Array.isArray(item?.dietaries)
            ? item.dietaries
            : typeof item?.dietaries === "string" && item.dietaries
              ? [item.dietaries]
              : [],
          membersPrice: typeof item?.membersPrice === "string" ? item.membersPrice : "",
          nonMembersPrice: typeof item?.nonMembersPrice === "string" ? item.nonMembersPrice : "",
        }));
        return {
          ...menu,
          items: normalizedItems,
          venueOutlet: Array.isArray(menu.venueOutlet) ? menu.venueOutlet : [],
        };
      }
      if (typeof menu.items === "string") {
        const items = menu.items
          .split(",")
          .map((item: string) => item.trim())
          .filter(Boolean)
          .map((name: string) => ({
            name,
            photos: [],
            dietaries: [],
            membersPrice: "",
            nonMembersPrice: "",
          }));
        return {
          ...menu,
          items,
          venueOutlet: Array.isArray(menu.venueOutlet) ? menu.venueOutlet : [],
        };
      }
      return {
        ...menu,
        items: [],
        venueOutlet: Array.isArray(menu.venueOutlet) ? menu.venueOutlet : [],
      };
    });
  } catch {
    return [];
  }
};

const saveMenus = (menus: SpecialOccasionMenu[]) => {
  localStorage.setItem(storageKey, JSON.stringify(menus));
};

const emptyForm = {
  eventName: "",
  startDate: "",
  endDate: "",
  venue: [] as string[],
  venueOutlet: [] as string[],
  mealType: "",
  items: [
    {
      name: "",
      photos: [] as MenuItemPhoto[],
      dietaries: [] as string[],
      membersPrice: "",
      nonMembersPrice: "",
    },
  ],
};

const mealTypeOptions = [
  { value: "Breakfast", label: "Breakfast" },
  { value: "Lunch", label: "Lunch" },
  { value: "Dinner", label: "Dinner" },
  { value: "Special Event", label: "Special Event" },
];

const normalizeIdsForPayload = (values: string[]) =>
  values
    .map((value) => {
      const trimmed = String(value).trim();
      if (!trimmed) return null;
      const asNumber = Number(trimmed);
      return Number.isNaN(asNumber) ? trimmed : asNumber;
    })
    .filter((value): value is string | number => value !== null);

export default function AddSpecialOccasionMenu() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [formData, setFormData] = useState(emptyForm);
  const [activePhoto, setActivePhoto] = useState<string | null>(null);
  const [venues, setVenues] = useState<any[]>([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [venueError, setVenueError] = useState("");
  const [venueOutletOptions, setVenueOutletOptions] = useState<MultiSelectOption[]>([]);
  const [venueOutletLoading, setVenueOutletLoading] = useState(false);
  const [venueOutletError, setVenueOutletError] = useState("");
  const [dietaryOptions, setDietaryOptions] = useState<MultiSelectOption[]>([]);
  const [dietaryLoading, setDietaryLoading] = useState(false);
  const [dietaryError, setDietaryError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState<Record<keyof typeof emptyForm, string>>({
    eventName: "",
    startDate: "",
    endDate: "",
    venue: "",
    venueOutlet: "",
    mealType: "",
    items: "",
  });



  useEffect(() => {
    if (Array.isArray(formData.items)) return;
    setFormData((prev) => ({
      ...prev,
      items: [
        {
          name: "",
          photos: [],
          dietaries: [],
          membersPrice: "",
          nonMembersPrice: "",
        },
      ],
    }));
  }, [formData.items]);

  const fetchVenues = async () => {
    if (!apiUrl) {
      setVenueError("API URL is not configured.");
      return;
    }
    setVenueLoading(true);
    setVenueError("");
    const response = await fetch(`${apiUrl}/venues`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: token || "",
      },
    });
    const result = await response.json();
    // console.log(result);
    
    if (result.success) {
      setVenues(result.venues);
    } else {
      setVenues([]);
      setVenueError("Failed to load venues.");
    }

    if (!response.ok) {
      setVenueLoading(false);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    setVenueLoading(false);
  };

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenueOutlets = async () => {
    if (!apiUrl) {
      setVenueOutletError("API URL is not configured.");
      return;
    }
    setVenueOutletLoading(true);
    setVenueOutletError("");

    const response = await fetch(`${apiUrl}/special_occasion_menus/venue_outlets`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: token || "",
      },
    });
    const result = await response.json();
    // console.log(result);
    

    if (result.success) {
      const options: MultiSelectOption[] = Array.isArray(result.venue_outlets)
        ? result.venue_outlets
            .map((venueOutlet: any) => ({
              value:
                typeof venueOutlet?.value === "string"
                  ? venueOutlet.value
                  : typeof venueOutlet?.venueoutlet_id === "number"
                    ? String(venueOutlet.venueoutlet_id)
                    : typeof venueOutlet?.venueoutlet_id === "string"
                      ? venueOutlet.venueoutlet_id
                      : typeof venueOutlet?.outlet_id === "number"
                        ? String(venueOutlet.outlet_id)
                        : typeof venueOutlet?.outlet_id === "string"
                          ? venueOutlet.outlet_id
                          : typeof venueOutlet?.venue_id === "number"
                            ? String(venueOutlet.venue_id)
                            : typeof venueOutlet?.venue_id === "string"
                              ? venueOutlet.venue_id
                              : typeof venueOutlet?.id === "number"
                                ? String(venueOutlet.id)
                                : typeof venueOutlet?.id === "string"
                                  ? venueOutlet.id
                      : typeof venueOutlet?.name === "string"
                        ? venueOutlet.name
                        : "",
              text:
                typeof venueOutlet?.label === "string"
                  ? venueOutlet.label
                  : typeof venueOutlet?.name === "string"
                    ? venueOutlet.name
                    : typeof venueOutlet?.venue_outlet_name === "string"
                      ? venueOutlet.venue_outlet_name
                    : "",
              selected: false,
            }))
            .filter((option: MultiSelectOption) => option.value && option.text)
        : [];
      setVenueOutletOptions(options);
    } else {
      setVenueOutletOptions([]);
      setVenueOutletError("Failed to load venue outlets.");
    }

    if (!response.ok) {
      setVenueOutletLoading(false);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    setVenueOutletLoading(false);
  };

  useEffect(() => {
    fetchVenueOutlets();
  }, []);

  const fetchDietaries = async () => {
    if (!apiUrl) {
      setDietaryError("API URL is not configured.");
      return;
    }
    setDietaryLoading(true);
    setDietaryError("");
    const response = await fetch(`${apiUrl}/special_occasion_menus/dietaries`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: token || "",
      },
    });
    const result = await response.json();
    // console.log(result);

    if (result.success) {
      const options: MultiSelectOption[] = Array.isArray(result.dietaries)
        ? result.dietaries
            .map((dietary: any) => ({
              value:
                typeof dietary?.dietary_id === "number"
                  ? String(dietary.dietary_id)
                  : typeof dietary?.dietary_id === "string"
                    ? dietary.dietary_id
                    : "",
              text: typeof dietary?.name === "string" ? dietary.name : "",
              selected: false,
            }))
            .filter((option: MultiSelectOption) => option.value && option.text)
        : [];
      setDietaryOptions(options);
    } else {
      setDietaryOptions([]);
      setDietaryError("Failed to load dietaries.");
    }

    if (!response.ok) {
      setDietaryLoading(false);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    setDietaryLoading(false);
  };

  useEffect(() => {
    fetchDietaries();
  }, []);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleMealTypeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, mealType: value }));
    setErrors((prev) => ({ ...prev, mealType: "" }));
  };

  const handleVenueChange = (values: string[]) => {
    setFormData((prev) => ({ ...prev, venue: values }));
    setErrors((prev) => ({ ...prev, venue: "" }));
  };

  const handleVenueOutletChange = (values: string[]) => {
    setFormData((prev) => ({ ...prev, venueOutlet: values }));
    setErrors((prev) => ({ ...prev, venueOutlet: "" }));
  };

  const handleItemChange = (index: number, value: string) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], name: value };
      return { ...prev, items: nextItems };
    });
    setErrors((prev) => ({ ...prev, items: "" }));
  };

  const handleItemDietariesChange = (index: number, values: string[]) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], dietaries: values };
      return { ...prev, items: nextItems };
    });
  };

  const handleItemPriceChange = (
    index: number,
    field: "membersPrice" | "nonMembersPrice",
    value: string
  ) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], [field]: value };
      return { ...prev, items: nextItems };
    });
  };

  const handleItemPhotoChange = (index: number, files?: FileList | null) => {
    if (!files || files.length === 0) return;
    const selectedFiles = Array.from(files);
    const hasInvalidFile = selectedFiles.some(
      (file) => !ALLOWED_PHOTO_TYPES.has(file.type) || file.size > MAX_PHOTO_FILE_SIZE_BYTES
    );
    if (hasInvalidFile) {
      setSubmitError("Photos must be JPG, JPEG, PNG, or WEBP and no larger than 5MB each.");
      return;
    }

    setSubmitError("");
    setFormData((prev) => {
      const nextItems = [...prev.items];
      const existing = nextItems[index]?.photos ?? [];
      const uploadedPhotos: MenuItemPhoto[] = selectedFiles.map((file) => ({
        previewUrl: URL.createObjectURL(file),
        file,
      }));
      nextItems[index] = {
        ...nextItems[index],
        photos: [...existing, ...uploadedPhotos],
      };
      return { ...prev, items: nextItems };
    });
    setErrors((prev) => ({ ...prev, items: "" }));
  };

  const handleRemoveItemPhoto = (itemIndex: number, photoIndex: number) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      const photoToRemove = nextItems[itemIndex].photos[photoIndex];
      if (photoToRemove?.file) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
      }
      const nextPhotos = nextItems[itemIndex].photos.filter((_, idx) => idx !== photoIndex);
      nextItems[itemIndex] = { ...nextItems[itemIndex], photos: nextPhotos };
      return { ...prev, items: nextItems };
    });
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          name: "",
          photos: [],
          dietaries: [],
          membersPrice: "",
          nonMembersPrice: "",
        },
      ],
    }));
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => {
      const nextItems = prev.items.filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, items: nextItems.length > 0 ? nextItems : prev.items };
    });
  };

  const closePhotoPreview = () => {
    setActivePhoto(null);
  };

  const getPhotoFileLabel = (photo: MenuItemPhoto) => {
    if (photo.file?.name) return photo.file.name;
    if (!photo.previewUrl) return "photo";
    const withoutQuery = photo.previewUrl.split("?")[0];
    const segments = withoutQuery.split("/");
    return decodeURIComponent(segments[segments.length - 1] || "photo");
  };

  const validate = () => {
    const nextErrors = { ...errors };

    if (!formData.eventName.trim()) nextErrors.eventName = "Event is required.";
    if (!formData.startDate) nextErrors.startDate = "Start date is required.";
    if (!formData.endDate) nextErrors.endDate = "End date is required.";
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      nextErrors.endDate = "End date must be on or after the start date.";
    }
    if (formData.venue.length === 0) nextErrors.venue = "Venue is required.";
    if (formData.venueOutlet.length === 0)
      nextErrors.venueOutlet = "Venue outlet is required.";
    if (!formData.mealType) nextErrors.mealType = "Meal type is required.";
    const validItems = formData.items.filter((item) => item.name.trim().length > 0);
    if (validItems.length === 0) nextErrors.items = "At least one item is required.";

    setErrors(nextErrors);

    return Object.values(nextErrors).every((value) => value === "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return;
    }

    setSubmitLoading(true);
    setSubmitError("");
    try {
      const validItems = formData.items.filter((item) => item.name.trim().length > 0);
      const itemsPayload = validItems.map((item) => ({
        name: item.name.trim(),
        dietaries: item.dietaries
          .map((dietaryId) => Number(dietaryId))
          .filter((dietaryId) => !Number.isNaN(dietaryId)),
        members_price: item.membersPrice ? Number(item.membersPrice) : 0,
        non_members_price: item.nonMembersPrice ? Number(item.nonMembersPrice) : 0,
      }));

      const multipartBody = new FormData();
      multipartBody.append("event_name", formData.eventName.trim());
      multipartBody.append("start_date", formData.startDate);
      multipartBody.append("end_date", formData.endDate);
      multipartBody.append("meal_type", formData.mealType);
      multipartBody.append(
        "venues",
        JSON.stringify(normalizeIdsForPayload(formData.venue))
      );
      multipartBody.append(
        "venue_outlets",
        JSON.stringify(normalizeIdsForPayload(formData.venueOutlet))
      );
      multipartBody.append("items", JSON.stringify(itemsPayload));
      validItems.forEach((item, itemIndex) => {
        item.photos.forEach((photo) => {
          if (!photo.file) return;
          multipartBody.append(`item_${itemIndex}`, photo.file, photo.file.name);
          multipartBody.append("photo_item_keys[]", `item_${itemIndex}`);
          if (validItems.length === 1) {
            multipartBody.append("photos[]", photo.file, photo.file.name);
          }
        });
      });

      const response = await fetch(`${apiUrl}/special_occasion_menus`, {
        method: "POST",
        headers: {
          token: token || "",
        },
        body: multipartBody,
      });
      const responseText = await response.text();
      let result: any = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message ||
            responseText ||
            `Failed to save menu (${response.status})`
        );
      }

      navigate("/special-occasion-menus");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to save menu.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/special-occasion-menus");
  };

  const formatDateAsLocalYmd = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (field: "startDate" | "endDate") => (date: Date[]) => {
    if (date.length > 0) {
      const formattedDate = formatDateAsLocalYmd(date[0]);
      setFormData((prev) => ({
        ...prev,
        [field]: formattedDate,
      }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <>
      <PageMeta
        title="Add Special Occasion Menu | Hospitality Genie"
        description="Create a special menu for a specific event or occasion."
      />
      
        <ComponentCard title="Add Special Occasion Menu">
          <Form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <div className="col-span-full">
                <h4 className="pb-4 text-base font-medium text-gray-800 border-b border-gray-200 dark:border-gray-800 dark:text-white/90">
                  Menu Details
                </h4>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Event</Label>
                    <Input
                      type="text"
                      name="eventName"
                      value={formData.eventName}
                      onChange={handleChange}
                    />
                    {errors.eventName && <p className="text-red-500 text-sm">{errors.eventName}</p>}
                  </div>
                  <div>
                    <Label>Meal Type</Label>
                    <Select
                      options={mealTypeOptions}
                      value={formData.mealType}
                      onChange={handleMealTypeChange}
                      className="mt-2 bg-gray-50 dark:bg-gray-800"
                    />
                    {errors.mealType && <p className="text-red-500 text-sm">{errors.mealType}</p>}
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <div className="relative flatpickr-wrapper w-full">
                      <Flatpickr
                        value={formData.startDate}
                        onChange={handleDateChange("startDate")}
                        options={{
                          dateFormat: "Y-m-d",
                        }}
                        className="w-full py-2 pl-3 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      />
                      <span className="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-3 top-1/2 dark:text-gray-400">
                        <CalenderIcon />
                      </span>
                    </div>
                    {errors.startDate && <p className="text-red-500 text-sm">{errors.startDate}</p>}
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <div className="relative flatpickr-wrapper w-full">
                      <Flatpickr
                        value={formData.endDate}
                        onChange={handleDateChange("endDate")}
                        options={{
                          dateFormat: "Y-m-d",
                          minDate: formData.startDate || undefined,
                        }}
                        className="w-full py-2 pl-3 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      />
                      <span className="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-3 top-1/2 dark:text-gray-400">
                        <CalenderIcon />
                      </span>
                    </div>
                    {errors.endDate && <p className="text-red-500 text-sm">{errors.endDate}</p>}
                  </div>
                  <div>
                    <Label>Venue</Label>
                    <MultiSelect
                      label=""
                      options={venues
                        .map((venue) => ({
                          value:
                            typeof venue?.cst === "string"
                              ? venue.cst
                              : typeof venue?.cst === "number"
                                ? String(venue.cst)
                                : "",
                          text: typeof venue?.name === "string" ? venue.name : "",
                          selected: false,
                        }))
                        .filter((option) => option.value && option.text)}
                      defaultSelected={formData.venue}
                      value={formData.venue}
                      onChange={handleVenueChange}
                      closeOnSelect
                      disabled={venueLoading || venues.length === 0}
                    />
                    {venueLoading && (
                      <p className="text-xs text-gray-500 mt-2">Loading venues...</p>
                    )}
                    {venueError && <p className="text-red-500 text-sm">{venueError}</p>}
                    {errors.venue && <p className="text-red-500 text-sm">{errors.venue}</p>}
                  </div>
                  <div>
                    <Label>Venue Outlets</Label>
                    <MultiSelect
                      label=""
                      options={venueOutletOptions}
                      defaultSelected={formData.venueOutlet}
                      value={formData.venueOutlet}
                      onChange={handleVenueOutletChange}
                      closeOnSelect
                      disabled={venueOutletLoading || venueOutletOptions.length === 0}
                    />
                    {venueOutletLoading && (
                      <p className="text-xs text-gray-500 mt-2">Loading venue outlets...</p>
                    )}
                    {venueOutletError && (
                      <p className="text-red-500 text-sm">{venueOutletError}</p>
                    )}
                    {errors.venueOutlet && (
                      <p className="text-red-500 text-sm">{errors.venueOutlet}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center justify-between">
                  <Label>Items</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    startIcon={<PlusIcon className="h-4 w-4" />}
                    onClick={handleAddItem}
                  >
                    Add Item
                  </Button>
                </div>
                <div className="mt-3 space-y-4">
                  {formData.items.map((item, index) => (
                    <div
                      key={`item-${index}`}
                      className="rounded-xl border border-gray-200 p-4 dark:border-gray-800"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-white/80">
                          Item {index + 1}
                        </p>
                        {formData.items.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            startIcon={<TrashBinIcon className="h-4 w-4" />}
                            className="!bg-red-500 !text-white !ring-red-500 hover:!bg-red-600 dark:!bg-red-500 dark:hover:!bg-red-600 dark:!ring-red-500"
                            onClick={() => handleRemoveItem(index)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <Label>Item Name</Label>
                          <textarea
                            name={`item-${index}-name`}
                            value={item.name}
                            onChange={(e) => handleItemChange(index, e.target.value)}
                            rows={2}
                            className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                          />
                        </div>
                        <div>
                          <Label>Dietaries</Label>
                          <MultiSelect
                            label=""
                            options={dietaryOptions}
                            defaultSelected={item.dietaries}
                            value={item.dietaries}
                            onChange={(values) => handleItemDietariesChange(index, values)}
                            closeOnSelect
                            disabled={dietaryLoading || dietaryOptions.length === 0}
                          />
                          {dietaryLoading && (
                            <p className="text-xs text-gray-500 mt-2">Loading dietaries...</p>
                          )}
                          {dietaryError && (
                            <p className="text-red-500 text-sm">{dietaryError}</p>
                          )}
                        </div>
                        <div>
                          <Label>Members Price $</Label>
                          <Input
                            type="number"
                            name={`item-${index}-members-price`}
                            value={item.membersPrice}
                            onChange={(e) =>
                              handleItemPriceChange(index, "membersPrice", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label>Non-Members Price $</Label>
                          <Input
                            type="number"
                            name={`item-${index}-non-members-price`}
                            value={item.nonMembersPrice}
                            onChange={(e) =>
                              handleItemPriceChange(index, "nonMembersPrice", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="mt-3">
                        <Label>Photos</Label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          multiple
                          onChange={(e) => handleItemPhotoChange(index, e.target.files)}
                          className="block w-full text-sm text-gray-600 file:mr-4 file:rounded-lg file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gray-700 hover:file:bg-gray-200 dark:text-gray-300 dark:file:bg-gray-800 dark:file:text-gray-200"
                        />
                      </div>

                      {item.photos.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                          {item.photos.map((photo, photoIndex) => (
                            <div key={`${index}-photo-${photoIndex}`} className="relative">
                              <img
                                src={photo.previewUrl}
                                alt={item.name || `Item ${index + 1}`}
                                className="h-24 w-24 rounded-lg object-cover"
                                role="button"
                                tabIndex={0}
                                onClick={() => setActivePhoto(photo.previewUrl)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    setActivePhoto(photo.previewUrl);
                                  }
                                }}
                              />
                              <p className="mt-1 w-24 truncate text-xs text-gray-500 dark:text-gray-400">
                                {getPhotoFileLabel(photo)}
                              </p>
                              <button
                                type="button"
                                onClick={() => handleRemoveItemPhoto(index, photoIndex)}
                                aria-label="Remove photo"
                                className="absolute -right-2 -top-2 inline-flex items-center justify-center rounded-full bg-red-500 p-1.5 text-white hover:bg-red-600"
                              >
                                <TrashBinIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {errors.items && <p className="mt-3 text-red-500 text-sm">{errors.items}</p>}
              </div>
              <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-800">
                <Button type="submit" size="sm" disabled={submitLoading}>
                  {submitLoading ? "Saving..." : "Save Menu"}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                {submitError && (
                  <p className="w-full text-sm text-red-500">{submitError}</p>
                )}
              </div>
            </div>
          </Form>
        </ComponentCard>
      {activePhoto && (
        <div
          className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/70 p-6"
          onClick={closePhotoPreview}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="max-h-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activePhoto}
              alt="Selected menu item"
              className="max-h-[80vh] w-auto rounded-xl object-contain shadow-lg"
            />
          </div>
        </div>
      )}
    </>
  );
}
